import { useQuery } from '@tanstack/react-query';
import type {
  AgAreaSeriesOptions,
  AgBarSeriesOptions
} from 'ag-charts-enterprise';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { atom, useAtom } from 'jotai';
import { groupBy, sortBy } from 'lodash';
import { useEffect } from 'react';

import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import {
  PURPLESCALE_COLORS,
  VMP_OUTLINED_FILLED_COLORS,
  VMP_VULNERABILITY_COLORS
} from '@/constants/chartcolors.constants';
import { CRITICALITY_LABELS } from '@/constants/criticalitylabels.constants';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  vmpAssetsAtom,
  vmpVulnerabilitiesAtom
} from '@/pages/VulnerabilityManagement/VulnerabilityManagement.store';
import type { VulnerabilityType } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import calcGroupBy from '@/utils/Common.utils';

import dashboardTimeFrameAtom from '../Dashboard.store';
import type {
  RemediationMetricsDTO,
  VulnerabilityMetricsDTO
} from '../Dashboard.types';
import { getDates, processAndTruncateOSAssets } from '../Dashboard.utils';

const now = dayjs();

export const vulnerabilityMetricsAtom = atom<VulnerabilityMetricsDTO[]>([]);
export const remediationMetricsAtom = atom<RemediationMetricsDTO[]>([]);

export const dateFilteredVulnerabilitiesAtom = atom((get) => {
  const vulnerabilities = get(vmpVulnerabilitiesAtom);
  const timeFrame = get(dashboardTimeFrameAtom);
  const endDate = dayjs().subtract(1, 'day');

  return vulnerabilities.filter(
    (vuln) =>
      dayjs
        .utc(vuln.date_identified)
        .local()
        .isAfter(endDate.subtract(timeFrame, 'days')) &&
      dayjs.utc(vuln.date_identified).local().isBefore(endDate)
  );
});

export const dateFilteredAssetsAtom = atom((get) => {
  const assets = get(vmpAssetsAtom);
  const timeFrame = get(dashboardTimeFrameAtom);

  return assets.filter((asset) =>
    dayjs
      .utc(
        (
          asset.external_details?.[0]?.data as
            | { created_in_shield?: string }
            | undefined
        )?.created_in_shield || undefined
      )
      .local()
      .isAfter(now.subtract(timeFrame, 'days'))
  );
});

export const vulnerabilitiesLevelGroupByAtom = atom<PercentageBarDataType[]>(
  (get) => {
    const vulnerabilities = get(dateFilteredVulnerabilitiesAtom);

    const allLevels = ['Critical', 'High', 'Medium', 'Low'];

    const results: PercentageBarDataType[] = allLevels.map((level) => {
      const count = vulnerabilities.filter(
        (vuln: VulnerabilityType) => vuln.cvss_level === level
      ).length;

      return {
        name: level,
        data: [count],
        color:
          VMP_VULNERABILITY_COLORS[
            level.toUpperCase() as keyof typeof VMP_VULNERABILITY_COLORS
          ],
        type: 'count'
      };
    });
    return results;
  }
);

export const vulnerabilitiesOverTimeAtom = atom<AgAreaSeriesOptions['data']>(
  (get) => {
    const vulnerabilityMetrics = get(vulnerabilityMetricsAtom);

    const timeFrame = get(dashboardTimeFrameAtom);
    const endDate = dayjs().subtract(1, 'day');
    const dates = getDates(endDate, timeFrame);

    // Create an array of objects, one for each date
    return dates.map((date: string) => {
      // Initialize object with all required properties
      const dateObj: { [key: string]: number | Date } = {
        date: dayjs(date).toDate(),
        CRITICAL: 0,
        HIGH: 0,
        MEDIUM: 0,
        LOW: 0
      };

      // For each severity, count the vulnerabilities for this date
      Object.keys(CRITICALITY_LABELS).forEach((severity) => {
        const vulnerabilitiesByDateAndSeverity = vulnerabilityMetrics.filter(
          (vulnerability) =>
            vulnerability.severity.toUpperCase() === severity &&
            dayjs(vulnerability.date).format(YEAR_MONTH_DAY_FORMAT) === date
        );
        dateObj[severity] = vulnerabilitiesByDateAndSeverity[0]?.count || 0;
      });

      return dateObj;
    });
  }
);

export const assetsByImportanceAtom = atom((get) => {
  const assets = get(dateFilteredAssetsAtom);

  let results = calcGroupBy(assets, 'criticality');

  results = results.map((namedResult) => ({
    ...namedResult,
    color:
      VMP_OUTLINED_FILLED_COLORS[
        (namedResult.name.toUpperCase() as keyof typeof VMP_OUTLINED_FILLED_COLORS) ||
          'OPEN'
      ]
  }));

  const statusOrder = Object.keys(VMP_OUTLINED_FILLED_COLORS);
  return sortBy(results, (result) =>
    statusOrder.indexOf(result.name.toUpperCase())
  );
});

export const assetsByOSAtom = atom((get) => {
  const assets = get(dateFilteredAssetsAtom);

  const rawResults = calcGroupBy(assets, 'operating_system').map(
    ({ data, ...result }, index) => ({
      ...result,
      color: PURPLESCALE_COLORS[index],
      count: data[0]
    })
  );

  return processAndTruncateOSAssets(rawResults);
});

export const assetsByScannerTypeAtom = atom((get) => {
  const assets = get(dateFilteredAssetsAtom);

  const shieldProviderOnly = assets.map((asset) => ({
    ...asset,
    external_details:
      asset.external_details?.filter(
        (detail) => detail.provider === 'shield'
      ) ?? []
  }));

  // Flatten and group Shield external_details by shield_type
  const flattenedDetails = shieldProviderOnly.flatMap(
    (asset) =>
      asset.external_details?.map((detail) => ({
        ...detail,
        shield_type:
          (detail.data as Record<string, unknown>)?.scanner_type || 'Unknown'
      })) ?? []
  );

  const results = calcGroupBy(flattenedDetails, 'shield_type').map(
    ({ data, ...result }, index) => ({
      ...result,
      color: `var(--mui-palette-chartColors-${index})`,
      count: data[0]
    })
  );

  return results;
});

export const assetsByVulnerabilitiesAtom = atom((get) => {
  const assets = get(dateFilteredAssetsAtom);

  const groupByData = groupBy(assets, 'ip_address');
  const results: AgBarSeriesOptions['data'] = [];
  Object.keys(groupByData).forEach((ip) => {
    const totalVulnerabilities = groupByData[ip].reduce(
      (acc, current) => acc + (current.vulnerabilities?.length || 0),
      0
    );
    results.push({
      name: ip,
      count: totalVulnerabilities,
      color: PURPLESCALE_COLORS[0] // You can assign a color based on your requirements
    });
  });
  const sortedResults = sortBy(results, (d) =>
    d.count ? d.count : 0
  ).reverse();

  return sortedResults.slice(0, 10);
});

export const remediationsPerDayAtom = atom<AgBarSeriesOptions['data']>(
  (get) => {
    const remediationMetrics = get(remediationMetricsAtom);
    const timeFrame = get(dashboardTimeFrameAtom);
    const endDate = dayjs().subtract(1, 'day');
    const dates = getDates(endDate, timeFrame);

    // Create an array of objects, one for each date
    return dates.map((date: string) => {
      // Initialize object with all required properties
      const dateObj: { [key: string]: number | Date } = {
        date: dayjs(date).toDate(),
        Critical: 0,
        High: 0,
        Medium: 0,
        Low: 0
      };

      // For each priority, count the remediations for this date
      Object.values(CRITICALITY_LABELS).forEach((criticality) => {
        const remediationsByDateAndSeverity = remediationMetrics.filter(
          (remediation) =>
            remediation.severity === criticality &&
            dayjs(remediation.date).format(YEAR_MONTH_DAY_FORMAT) === date
        );

        dateObj[criticality] = remediationsByDateAndSeverity[0]?.count;
      });

      return dateObj;
    });
  }
);

export const useFetchVulnerabilityMetrics = () => {
  const handleError = useHandleError();
  const [organization] = useAtom(selectedOrganizationAtom);
  const [, setVulnerabilityMetrics] = useAtom(vulnerabilityMetricsAtom);

  const { data, isError, error, refetch, isLoading, isSuccess } = useQuery({
    queryKey: [
      `organizations/${organization.label}/vmpe_metrics/vulnerabilities`,
      organization
    ],
    staleTime: 60 * 1000 // 1 minute
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch vulnerability metrics.'
      );
    }
    if (data && isSuccess) {
      setVulnerabilityMetrics(data as VulnerabilityMetricsDTO[]);
    }
  }, [isError, isSuccess, data]);

  return {
    isLoading,
    refetch
  };
};

export const useFetchRemediationMetrics = () => {
  const handleError = useHandleError();
  const [, setRemediationMetrics] = useAtom(remediationMetricsAtom);
  const [organization] = useAtom(selectedOrganizationAtom);

  const { data, isError, error, refetch, isLoading, isSuccess } = useQuery({
    queryKey: [
      `organizations/${organization.label}/vmpe_metrics/remediations`,
      organization
    ],
    staleTime: 60 * 1000 // 1 minute
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch remediation metrics.');
    }
    if (data && isSuccess) {
      setRemediationMetrics(data as RemediationMetricsDTO[]);
    }
  }, [isError, data, isSuccess]);

  return {
    isLoading,
    refetch
  };
};
